import {
  pushDataLayerEvent,
  track,
  usePostHubspotEvent,
  usePostHubspotProperty,
} from "@/features/analytics";
import { FEATURE_FLAGS, isOnFeatureFlag } from "@lib/feature-flags";
import { useCollaborationStatus } from "@/features/collaboration/hooks/useCollaborationStatus";
import { useHocuspocusEnabled } from "@/features/collaboration/yjs/use-hocuspocus-enabled";
import { useYjsSyncStatus } from "@/features/collaboration/yjs/yjs-sync-context";
import {
  invertFlatData,
  isStructuredLayout,
  openProDialog,
  openStarterDialog,
  openTeamDialog,
} from "@/shared";
import { DEFAULT_FAVICON } from "@/shared/constants";
import {
  pagesApi,
  useSaveAndPublishPageMutation,
  useUpdatePageMutation,
} from "@/shared/services";
import {
  addCollabBreadcrumb,
  captureSentryError,
} from "@/shared/utils/sentry-helpers";
import { getStoryDisplayTitle } from "@/shared/utils/story-display-title";
import { api } from "@api/api";
import {
  useCreateVersionMutation,
  useUpdateVersionInDatabaseMutation,
  useUpdateVersionMutation,
} from "@api/versions";
import { useLockedAction } from "@components/plan-subscription-lock";
import { useAccountStore } from "@features/account-management";
import {
  blockedVariablesFromApiError,
  findBlockedVariables,
  highestRequiredPlan,
  openUpgradeToPublishDialog,
} from "@features/dynamic-variables/plan-rules";
import type { StoryType, WholeStoryType } from "@config";
import { useAppDispatch, useAppSelector } from "@store";
import { getAccount, isFirstPublish } from "@store/account/selectors";
import { handleAutoThumbnailVersionId } from "@store/share-dialog";
import { store } from "@store/store";
import { updateStoryByKeys } from "@store/story";
import { produce } from "immer";
import moment from "moment";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { PublishStatus } from "../rendering-provider/types";
import {
  generateFsPlaceholders,
  isPageChanged,
} from "../rendering-provider/utils";
import { useGenerateCanonical } from "./use-generate-canonical";
import { useGetStoryFastVersionVars } from "./use-get-story-fast-version-vars";
import { versionOrigin } from "@/features/standalone-versions/standalone-version";
import {
  PREVIEW_PARKED,
  useUpdatePreviewOnly,
} from "./use-update-preview-only";
import { publishVariablesSummary } from "@features/dynamic-variables/analytics/variables-analytics";

/**
 * The title the PRIMARY version is born with, at first publish.
 *
 * The primary version is the story's own share link, so its `data.title` has to
 * be the story's name — the backend titles the link, the published `<title>`
 * and the Open Graph record from that field, never from story settings
 * (`utils/version-link-title.js`).
 *
 * Every publish AFTER the first already re-syncs it server-side from
 * `settings.title` (`pages/service.js#updatePrimaryVersion`, falling back to
 * `settings.og.siteName` only when the story has no name of its own). This
 * seed is the one place that did not follow that rule: it took the legacy
 * "Public title" outright, so the first publish disagreed with every publish
 * after it, and a story published once and never republished served its Public
 * title on the share link forever.
 *
 * The precedence here is deliberately the SAME as `getStoryDisplayTitle` on the
 * server — story name first, Public title only as a fallback for a story that
 * never got one — so the value cannot change under the version the moment it is
 * republished. `undefined` means "no usable name": the backend treats a missing
 * title as "leave it alone" rather than erasing it.
 */
export const getPrimaryVersionTitle = getStoryDisplayTitle;

export const usePublish = () => {
  const dispatch = useAppDispatch();
  const account = useAppSelector(getAccount);
  const { mutate: postHubspotEvent } = usePostHubspotEvent();
  const { mutate: postHubspotProperty } = usePostHubspotProperty();
  // ====== STORY ======
  const isAfterFirstPublish = useAppSelector(isFirstPublish);
  const { mutateAsync: saveAndPublishPage } = useSaveAndPublishPageMutation();
  const { mutateAsync: updatePage } = useUpdatePageMutation();
  const handleVariables = useGetStoryFastVersionVars();
  const { generateCanonical } = useGenerateCanonical();
  const { isLocked, plan, value } = useLockedAction({
    feature: "live-stories",
  });
  // ====== STORY ======

  //  ====== VERSION ======
  const [createVersion] = useCreateVersionMutation();
  const [updateVersion] = useUpdateVersionMutation();
  const [updateVersionInDatabase] = useUpdateVersionInDatabaseMutation();
  //  ====== VERSION ======

  const isCollaborative = useCollaborationStatus();
  const hocuspocusEnabled = useHocuspocusEnabled();
  // The collaborative publish branch persists content by letting the backend
  // flush the Y.Doc (sends slides:undefined, reads contentPointer back). Without
  // Hocuspocus enabled, there is no Y.Doc, so collaborative (broadcast) accounts
  // must publish the full story like the non-collab path. Gate on Hocuspocus
  // actually running, mirroring use-autosave.
  const usesYjsPersistence = isCollaborative && hocuspocusEnabled;
  const { waitForConverged } = useYjsSyncStatus();
  const updatePreviewOnly = useUpdatePreviewOnly();

  const isStoriesCountLimit = async () => {
    if (!isLocked) return false;
    if (typeof value !== "number") return false;
    try {
      const result = await pagesApi.getPagesCount({ status: "Live" });
      return result?.count >= value;
    } catch (error) {
      track("failed to get pages count", { error });
      return false;
    }
  };

  const updateStoryAndDispatch = useCallback(
    async (page: StoryType, skip: boolean) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { settings: _, slides: __, ...updatedStory } = page;
      track(
        "success publish",
        publishVariablesSummary(
          page.settings ?? store.getState().story.story?.settings,
          store.getState().story.flatContentLayers,
        ),
      );
      if (!isAfterFirstPublish) {
        const date = moment(new Date()).utc().startOf("day");
        postHubspotProperty({ first_story_published_date: date.valueOf() });
        pushDataLayerEvent("conversion", {
          "event-action": "first link created",
        });
        postHubspotEvent({
          event: "first story published",
          props: {
            date: moment().format("DD/MM/YYYY hh:mm"),
          },
        });
        track("first publish");
      }
      if (skip) {
        triggerInvalidateCache();
        // Whole-story convergence: when the published story is the one open
        // in the editor, the fresh status must land in Redux AND broadcast
        // into the Y.Doc — otherwise the doc's stale status wins the next
        // reconcile and everyone (the publisher included) snaps back to
        // Draft. Only the publish-status fields are dispatched; the full
        // updatedStory stays skipped because the payload-story flow's
        // snapshot could clobber newer collaborative state.
        const openStoryId = store.getState().story.story?._id;
        if (openStoryId && openStoryId === page._id) {
          const { status, publishedAt, primaryVersionId } = updatedStory;
          const data = Object.fromEntries(
            Object.entries({ status, publishedAt, primaryVersionId }).filter(
              ([, value]) => value !== undefined,
            ),
          );
          if (Object.keys(data).length > 0) {
            dispatch(updateStoryByKeys({ data }));
          }
        }
        return { status: PublishStatus.Success, id: page._id, page };
      }
      dispatch(updateStoryByKeys({ data: { ...updatedStory } }));
      return { status: PublishStatus.Success, id: page._id, page };
    },
    [dispatch, isAfterFirstPublish, postHubspotEvent, postHubspotProperty],
  );

  const handlePublish = async (payload?: {
    force: boolean;
    story?: StoryType;
  }): Promise<{ status: string; id?: string; page?: StoryType }> => {
    try {
      let wholeStory = {} as WholeStoryType;
      const isPayloadStory = !!payload?.story;
      if (!isPayloadStory) {
        const { story: partial, flatContentLayers } = store.getState().story;
        if (!partial) return { status: PublishStatus.Error };
        const slides = invertFlatData({ flatData: flatContentLayers });
        wholeStory = { ...partial, slides };
      } else {
        if (!isStructuredLayout(payload?.story?.layoutVersion || "")) {
          return { status: PublishStatus.Confirmation };
        }
        const contentPointer = payload.story?.settings?.contentPointer;
        if (!contentPointer) return { status: PublishStatus.Error };
        const response = await fetch(contentPointer);
        const content = await response.json();
        wholeStory = {
          ...payload.story,
          slides: content.slides,
        } as WholeStoryType;
      }

      const isLive = wholeStory?.status === "Live";

      if (isLive && !payload?.force) {
        if (!isPageChanged(wholeStory))
          return {
            status: PublishStatus.Success,
            id: wholeStory._id,
            page: wholeStory,
          };
        else return { status: PublishStatus.Confirmation };
      }

      const isLimitReached = !isLive ? await isStoriesCountLimit() : false;

      if (isLimitReached) {
        if (plan === "Starter") openStarterDialog("publish stories limitation");
        else if (plan === "Pro") openProDialog("publish stories limitation");
        else if (plan === "Team") openTeamDialog("publish stories limitation");
        return { status: PublishStatus.StoriesLimit };
      }

      track("click story publish");
      const variables = handleVariables({ story: wholeStory });

      // Plan gate (pre-flight; dotell-backend enforces the same rules on
      // version create/update). Nothing plan-related is baked into the
      // template — the check runs against the live account every publish.
      const parsedManifest: unknown = JSON.parse(variables);
      const blockedVariables = findBlockedVariables(
        Array.isArray(parsedManifest)
          ? (parsedManifest as Array<{ name: string; type?: string }>)
          : [],
        useAccountStore.getState().planType,
      );
      if (blockedVariables.length > 0) {
        track("publish blocked by variables plan", {
          variables: blockedVariables.map((v) => v.name),
          "variables count": blockedVariables.length,
          "required plan": highestRequiredPlan(blockedVariables),
        });
        openUpgradeToPublishDialog(blockedVariables, "publish variables plan");
        return { status: PublishStatus.PlanRequired };
      }

      const story = produce(wholeStory, (draft) => {
        draft.settings.favicon =
          draft.settings.favicon || account?.favicon || DEFAULT_FAVICON;

        draft.layoutVersion = window.BUNDLE_VERSION;
        draft.settings.fs["fs-vars"] = variables;
        draft.slides = [];
        draft.settings.publicContentPointer =
          wholeStory?.settings?.contentPointer;
        if (!Object.hasOwn(draft.settings, "gatedContent"))
          draft.settings.gatedContent = null;
      });

      await generateCanonical(wholeStory, wholeStory.settings.canonical); // generate canonical URL

      if (usesYjsPersistence) {
        // The pointer scope this editor session autosaves in. In the master-
        // story editor `state.story.version` is undefined, so autosave records
        // on the PAGE lineage; only the version editor records on a version.
        // Publish must record on the SAME lineage — see step 1.
        const editorVersionId = store.getState().story.version?._id;

        // 0. Wait for the doc to converge before snapshotting. The forced
        //    snapshot below is built from THIS publisher's Redux state; if a
        //    co-editor's latest edits are still in flight (or our own writes
        //    aren't yet acknowledged), publishing now would repoint
        //    contentPointer at a snapshot missing those edits — reverting the
        //    deck to an older version on reopen (Monday #12337353624). Bounded
        //    wait: if it doesn't settle we still publish (foreground action
        //    must not hang) — the monotonic guard remains the backstop.
        const converged = await waitForConverged();
        if (!converged) {
          // Publish went ahead before the doc reported synced — the one window
          // where a co-editor's in-flight edit could still be missed. Breadcrumb
          // for the Sentry trail (rides on any error captured this session) +
          // track() for a standalone metric (it's not an error itself).
          addCollabBreadcrumb("publish before Yjs convergence (sync timeout)", {
            storyId: wholeStory._id,
            versionId: editorVersionId ?? null,
          });
          track("collab publish before convergence", {
            storyId: wholeStory._id,
          });
        }

        // 1. Force a fresh preview render BEFORE flipping publish status.
        //    Otherwise the published version inherits whatever previewUrl was
        //    last autosaved (up to 3s stale, or older if the autosave debounce
        //    never fired). updatePreviewOnly dispatches updateStoryByKeys
        //    internally, so the new previewUrl also broadcasts to other peers
        //    via the same Yjs update-story path used by editing.
        //
        //    Recorded under `editorVersionId`, NOT `story.primaryVersionId`.
        //    This used to pass the primary version's id while use-autosave
        //    passes `version?._id` (undefined in the master-story editor), so a
        //    single editing session wrote two lineages: autosave advanced
        //    page.settings.contentPointer, publish advanced
        //    version.contentPointer. The two shared neither lastKnownPointer
        //    nor the runExclusive queue, and the own-pointer registry is keyed
        //    per scope, so nothing could recognise the other's head. Then the
        //    backend's publishCollaborative -> updatePrimaryVersion mirrored
        //    the PAGE pointer onto version.contentPointer (its intended job:
        //    the primary version is a read-only mirror of the published page),
        //    which made the version head foreign to the version-scoped parent
        //    this call remembered — every Share after the first parked with
        //    "Couldn't publish — newer changes were saved elsewhere" until a
        //    reload re-seeded the scope (prod, story 6a6c576458b353916a9e4f19,
        //    2026-09-08). Same scope as autosave = same parent, same queue,
        //    same registry.
        const previewResult = await updatePreviewOnly({
          story: wholeStory,
          versionId: editorVersionId,
          // Publish is foreground/authoritative — always publish the rendered
          // artifact (never discard on a concurrent edit).
          force: true,
        });

        // Parked: updatePreviewOnly already showed the "Couldn't publish"
        // toast (it knows WHY — stale parent pointer — better than we would
        // here), so just abort. Previously this result was ignored entirely,
        // so a park during publish showed that error toast AND published the
        // story anyway with a stale/mismatched contentPointer.
        if (previewResult === PREVIEW_PARKED) {
          return { status: PublishStatus.Error };
        }

        // 1b. Mirror the fresh previewUrl onto the primary version. This is
        //     the one thing the version-scoped call above used to buy us
        //     (commit 299d08ee3): the backend's updatePrimaryVersion rewrites
        //     the version's data/contentPointer on publish but never its
        //     previewUrl, so without this the published version's thumbnail
        //     lags the content. Presentation metadata only — deliberately NO
        //     contentPointer/parentPointer, so this write never touches the
        //     version's CAS head. Skipped when the editor IS the primary
        //     version: the hook already persisted previewUrl there. Best-
        //     effort: a stale thumbnail must not fail the publish.
        if (
          typeof previewResult === "string" &&
          story.primaryVersionId &&
          story.primaryVersionId !== editorVersionId
        ) {
          try {
            await updateVersionInDatabase({
              versionId: story.primaryVersionId,
              payload: { previewUrl: previewResult },
            }).unwrap();
          } catch (error) {
            addCollabBreadcrumb("primary version previewUrl mirror failed", {
              storyId: wholeStory._id,
              versionId: story.primaryVersionId,
              message: error instanceof Error ? error.message : String(error),
            });
          }
        }

        // 2. Backend flushes Yjs (contentPointer) and uses the freshly-written
        //    pointer. We do NOT send slides — that would race the merged Yjs state.
        const minimalPayload = {
          ...story,
          slides: undefined,
          collaborative: true,
        };
        const page = await saveAndPublishPage({
          id: story._id,
          data: minimalPayload as any,
        });

        // 2b. Align the contentPointer with the freshly-flushed one the backend
        //     just wrote, and broadcast it to peers. Whole-story convergence:
        //     every user must end up with the same pointer after a publish, so
        //     this goes out via the Yjs update-story path (NOT broadcast:true).
        const freshContentPointer = (page as StoryType | undefined)?.settings
          ?.contentPointer;
        dispatch(
          updateStoryByKeys({
            data: {
              settings: {
                // The parsed dynamic-variables string ("fs-vars") is computed
                // at publish time and was previously only sent to the backend.
                // It must also land in Redux + the Y.Doc so the Share dialog's
                // variables list works for this user AND for peers (who build
                // the dialog from their own Redux story).
                fs: story.settings.fs,
                ...(freshContentPointer
                  ? {
                      contentPointer: freshContentPointer,
                      publicContentPointer: freshContentPointer,
                    }
                  : {}),
              },
            },
          }),
        );

        // 3. Invalidate the Stories list cache so dashboards / list views on
        //    any tab that hits this user's RTK Query cache see the new status.
        //    The Redux-level state.story update propagates via
        //    updateStoryByKeys -> Yjs -> other peers' syncFromYjs separately.
        dispatch(api.util.invalidateTags(["Stories"]));

        if (story.primaryVersionId) {
          dispatch(
            api.util.invalidateTags([
              { type: "Version", id: story.primaryVersionId },
            ]),
          );
          return updateStoryAndDispatch(page, isPayloadStory);
        }

        const newVersion = await createVersion({
          id: page._id,
          data: {
            title: getPrimaryVersionTitle(page.settings),
            ogImage:
              "https://www.storydoc.com/assets/images/branding/og-storydoc.gif",
            password: null,
            isPrimaryVersion: true,
            collaborative: true,
            ...generateFsPlaceholders(variables),
          },
        }).unwrap();

        const updated = await updatePage({
          id: page._id,
          data: {
            ...page,
            primaryVersionId: newVersion._id,
            timestamps: false,
            publishedAt: newVersion.updatedAt,
          },
        });
        dispatch(handleAutoThumbnailVersionId(newVersion._id));
        return updateStoryAndDispatch(
          updated as unknown as StoryType,
          isPayloadStory,
        );
      }

      const page = await saveAndPublishPage({ id: story._id, data: story });
      if (story.primaryVersionId) {
        dispatch(
          api.util.invalidateTags([
            { type: "Version", id: story.primaryVersionId },
          ]),
        );
        return updateStoryAndDispatch(page, isPayloadStory);
      }

      const newVersion = await createVersion({
        id: page._id,
        data: {
          title: getPrimaryVersionTitle(page.settings),
          ogImage:
            "https://www.storydoc.com/assets/images/branding/og-storydoc.gif",
          password: null,
          isPrimaryVersion: true,
          ...generateFsPlaceholders(variables),
        },
      }).unwrap();

      const updated = await updatePage({
        id: page._id,
        data: {
          ...page,
          primaryVersionId: newVersion._id,
          timestamps: false,
          publishedAt: newVersion.updatedAt,
        },
      });
      dispatch(handleAutoThumbnailVersionId(newVersion._id));
      return updateStoryAndDispatch(
        updated as unknown as StoryType,
        isPayloadStory,
      );
    } catch (error) {
      // Backend refused for plan reasons (stale client or API-only path):
      // same dialog, no generic error toast.
      const blockedByBackend = blockedVariablesFromApiError(error);
      if (blockedByBackend) {
        openUpgradeToPublishDialog(blockedByBackend, "publish variables plan");
        return { status: PublishStatus.PlanRequired };
      }
      track("failed share story", { error });
      toast.error("Failed to share story");
      captureSentryError(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: {
            feature: "publish",
            operation: "handlePublish",
          },
          extra: {
            storyId: payload?.story?._id,
            force: payload?.force,
          },
          fingerprint: ["publish", "handlePublish-failure"],
        },
      );
      return { status: PublishStatus.Error };
    }
  };

  /** Republishes the open version; false when it failed (already toasted). */
  const updatePublishedVersion = useCallback(async (): Promise<boolean> => {
    const version = store.getState().story.version;
    try {
      if (!version) return false;
      // Collab: dynamic-variable values (the in-editor popover) live in
      // version.data, which is synced to peers via Yjs but — unlike non-collab
      // — is NEVER persisted to the version record (the collab autosave path
      // writes only preview metadata, and a remote-applied value doesn't even
      // trigger a local autosave). The server renders the shared link from the
      // DB's version.data (updateEditorVersion → getHandlebarsDataStructure),
      // so without this the link bakes in stale values. Push the current
      // (converged) version.data through the plain persist endpoint BEFORE the
      // render call so the link reflects everyone's latest edits. Mirrors how
      // non-collab keeps version.data current via autosave.
      // The title is edited through story.settings.title — the editor header
      // writes story settings, and useGetStory seeds them from
      // version.data.title when the version is opened. The autosave that copies
      // it back into version.data only runs on the non-collab path; the collab
      // one returns after refreshing preview metadata. So a rename reaches the
      // content blob but never the version record, and the shared link — which
      // the backend titles from version.data.title — keeps the old name.
      const { story: partial, flatContentLayers } = store.getState().story;
      const slides = invertFlatData({ flatData: flatContentLayers });
      const template = {
        ...structuredClone(partial),
        slides,
      } as WholeStoryType;
      // A version made by "Create new" keeps its own variable list (its page
      // may have none: the hidden host, or a story never published), so it is
      // re-read from the content on every update, as a story's is on publish.
      const standalone = Boolean(versionOrigin(version));
      if ((usesYjsPersistence || standalone) && version.data) {
        const versionTitle = partial?.settings?.title;
        await updateVersionInDatabase({
          versionId: version._id,
          payload: {
            data: {
              ...version.data,
              ...(typeof versionTitle === "string" && versionTitle.trim()
                ? { title: versionTitle }
                : {}),
              ...(standalone
                ? { fsVars: handleVariables({ story: template }) }
                : {}),
            },
          },
        }).unwrap();
      }

      const canonicalPath =
        version.canonical || `${template.settings.canonical}/${version._id}`;
      await generateCanonical(template, canonicalPath);

      // The PDF is regenerated server-side: this PUT reaches
      // updateEditorVersion, which re-renders the published story and — as
      // a content update — enqueues a background render. The reader polls
      // the baked pdf.json, so nothing here waits on it (mirrors create).
      // unwrap(): the RTK mutation resolves with {error} instead of
      // throwing — without it a failed PUT (no re-render, no PDF regen)
      // would skip the catch below and report success.
      // Send flow: keep the version's expiry (and so its expiry reminder),
      // and show one failure toast — the one below, not the mutation's too.
      await updateVersion({
        versionId: version._id,
        payload: {},
        ...(isOnFeatureFlag(FEATURE_FLAGS.SEND_FLOW) && {
          expireAt: version.expireAt ?? null,
          silent: true,
        }),
      }).unwrap();
      return true;
    } catch (error) {
      track("failed to update published version", { error });
      toast.error("Failed to update published version");
      return false;
    }
  }, [
    updateVersion,
    updateVersionInDatabase,
    // Depend on usesYjsPersistence (isCollaborative && hocuspocusEnabled), not
    // isCollaborative alone: the callback branches on the former, so if the
    // Hocuspocus flag resolves true after first render (cold cache) while
    // isCollaborative is unchanged, this must recompute or it keeps the stale
    // non-Yjs branch and skips the version-data persistence step.
    usesYjsPersistence,
    generateCanonical,
    handleVariables,
  ]);

  return { handlePublish, updatePublishedVersion };
};

const triggerInvalidateCache = () => {
  const isHome = window.location.pathname === "/pages/home";
  const tags = [{ type: "Stories", id: isHome ? "RECENT" : "SEARCH" }];
  const event = new CustomEvent("invalidate-stories-cache", {
    detail: { tags, options: { invalidate: true } },
  });
  document.dispatchEvent(event);
};
